/**
 * Draw area of interest — spec §F5.10: "draw AOI (click vertices → green polygon fill .10,
 * live `AOI · {km²} — open \"AOI insights & clip\"` tooltip (shoelace area); re-click clears
 * + releases clip)".
 *
 * Two responsibilities, deliberately split into two effects:
 *
 * * **the interaction** runs only while the tool is `active` — clicks push vertices,
 *   double-click closes the ring and hands the toolbar its button back, Esc cancels;
 * * **the polygon** is drawn from whatever ring is current, and survives the tool being
 *   switched off. That is the mockup's behaviour (`stopModes()` detaches the handlers but
 *   never removes `drawPoly`) and it is load-bearing: the §F5.10 clip, and the M4 AOI dock,
 *   act on an AOI the reader finished drawing minutes ago. Only re-clicking the tool — or
 *   the chip's own clear — takes it away.
 *
 * The ring is mirrored into §F1 `aoi` as soon as it is a polygon (≥3 vertices), with its
 * §G7 `shoelaceKm2` area, so the clip and the status bar need nothing else.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';

import { shoelaceKm2, type LatLon } from '../../lib/geo';
import { fmtKm2 } from '../../lib/format';
import { toast } from '../../lib/toast';
import { useAppStore } from '../../lib/state';
import { useMap } from './mapContext';
import { setToolArmed } from './panes';

export interface DrawAoiProps {
  active: boolean;
  /** Ring closed (double-click) or cancelled (Esc) — pop the toolbar button out. */
  onExit: () => void;
}

/**
 * §F5.10 "green polygon fill .10"; `#2E7D32` is `--brand`.
 *
 * `interactive: false` is the one addition to the mockup's options: the outline is an
 * annotation with no behaviour of its own, and leaving it as a mouse target made it
 * swallow the pointer while the reader was still adding vertices to it.
 */
export const AOI_STYLE = { color: '#2E7D32', weight: 2, fillOpacity: 0.1, interactive: false } as const;

/** A vertex this close to the previous one is the second click of a double-click. */
const DUPLICATE_PX = 6;

export function DrawAoi({ active, onExit }: DrawAoiProps) {
  const { t } = useTranslation();
  const map = useMap();
  const aoi = useAppStore((s) => s.aoi);
  const setAoi = useAppStore((s) => s.setAoi);
  const [pts, setPts] = useState<LatLon[]>([]);
  /**
   * The authoritative working ring. React state drives the render, but the Leaflet
   * handlers below are plain DOM listeners outside React's event system, so they read and
   * write the ref: it is correct no matter when React gets round to flushing.
   */
  const ptsRef = useRef<LatLon[]>([]);
  /** The one AOI outline; see the polygon effect for why it is never recreated. */
  const polyRef = useRef<L.Polygon | null>(null);
  const exitRef = useRef(onExit);
  exitRef.current = onExit;

  // Entering the tool starts a fresh ring; leaving it drops the working copy (the finished
  // one lives in the store from three vertices on).
  useEffect(() => {
    ptsRef.current = [];
    setPts([]);
  }, [active]);

  /* --------------------------------------------------------------- interaction */
  useEffect(() => {
    if (!active) return;

    const commit = (next: LatLon[]) => {
      ptsRef.current = next;
      setPts(next);
    };

    const onClick = (e: L.LeafletMouseEvent) => {
      commit([...ptsRef.current, [e.latlng.lat, e.latlng.lng]]);
    };
    const onDblClick = () => {
      // The two `click`s Leaflet fires before `dblclick` have already landed; the second is
      // a duplicate of the first at (near enough) the same pixel, so it is dropped.
      const next = [...ptsRef.current];
      if (next.length >= 2) {
        const a = map.latLngToContainerPoint(next[next.length - 1]!);
        const b = map.latLngToContainerPoint(next[next.length - 2]!);
        if (a.distanceTo(b) < DUPLICATE_PX) next.pop();
      }
      commit(next);
      // Published **here**, not left to the effect below: `exitRef.current()` flips
      // `active` false in the same React batch, so that effect runs with the new ring and
      // an already-false `active` and skips — which would leave the store holding the
      // un-deduplicated ring the second click produced.
      setAoi(next.length >= 3 ? { pts: next, areaKm2: shoelaceKm2(next) } : null);
      exitRef.current();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      commit([]);
      setAoi(null);
      exitRef.current();
    };

    const container = map.getContainer();
    container.style.cursor = 'crosshair';
    map.doubleClickZoom.disable();
    // A click is a vertex, not a unit selection, while the crosshair is armed.
    setToolArmed(map, true);
    map.on('click', onClick);
    map.on('dblclick', onDblClick);
    window.addEventListener('keydown', onKey);
    toast(t('tools.drawHint'));

    return () => {
      map.off('click', onClick);
      map.off('dblclick', onDblClick);
      window.removeEventListener('keydown', onKey);
      map.doubleClickZoom.enable();
      setToolArmed(map, false);
      container.style.cursor = '';
    };
  }, [active, map, setAoi, t]);

  /* ------------------------------------------------------- publish to the store */
  useEffect(() => {
    if (!active || pts.length < 3) return;
    setAoi({ pts, areaKm2: shoelaceKm2(pts) });
  }, [active, pts, setAoi]);

  /* -------------------------------------------------------------- the polygon */
  const ring = active && pts.length ? pts : (aoi?.pts ?? []);
  useEffect(() => {
    if (!ring.length) {
      if (polyRef.current) {
        map.removeLayer(polyRef.current);
        polyRef.current = null;
      }
      return;
    }
    // Created **once** and then updated in place. Recreating it per vertex churned the
    // `<path>` node, and a browser only synthesises `dblclick` when both clicks share a
    // target — so closing the ring with a double-click over the polygon silently did
    // nothing (NOTES #313). Cheaper, too.
    let poly = polyRef.current;
    if (!poly) {
      poly = L.polygon(ring, { ...AOI_STYLE }).addTo(map);
      poly.bindTooltip('', { permanent: true, className: 'unit-tip' });
      polyRef.current = poly;
    } else {
      poly.setLatLngs(ring);
    }
    if (ring.length >= 3) {
      poly.setTooltipContent(t('tools.aoiTooltip', { area: fmtKm2(shoelaceKm2(ring)) }));
      poly.openTooltip();
    } else {
      poly.closeTooltip();
    }
    // `ring` is rebuilt every render; the vertex list is what actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, t, JSON.stringify(ring)]);

  // The layer outlives every ring change, so it is torn down on unmount only.
  useEffect(
    () => () => {
      if (polyRef.current) {
        map.removeLayer(polyRef.current);
        polyRef.current = null;
      }
    },
    [map],
  );

  return null;
}

export default DrawAoi;
